import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Plus, Search, Loader2, Users, UserCheck, UserX, ShieldCheck, Mail, Phone,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import {
  listAdminUsers, updateAdminUserStatus,
  ADMIN_USER_TYPE_OPTIONS, labelFor, formatDateTime,
} from "../services/adminUsersService";
import { StatCard, SegmentedControl, Toast, getCurrentRole } from "../components/diagnostic/primitives";
import CreateAdminUserModal from "../components/adminUsers/CreateAdminUserModal";

const PAGE_SIZE = 20;

const KPI_CARDS = [
  { key: "total", label: "Total Admins", icon: Users, accent: { bg: "bg-blue-50", fg: "text-blue-600", bar: "bg-blue-500" } },
  { key: "active", label: "Active", icon: UserCheck, accent: { bg: "bg-emerald-50", fg: "text-emerald-600", bar: "bg-emerald-500" } },
  { key: "inactive", label: "Inactive", icon: UserX, accent: { bg: "bg-amber-50", fg: "text-amber-600", bar: "bg-amber-500" } },
  { key: "superadmins", label: "Super Admins", icon: ShieldCheck, accent: { bg: "bg-purple-50", fg: "text-purple-600", bar: "bg-purple-500" } },
];

const ROLE_BADGE = {
  wallet_superadmin: "bg-purple-50 text-purple-700 ring-purple-600/20",
  wallet_finance: "bg-blue-50 text-blue-700 ring-blue-600/20",
  wallet_ops: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  wallet_support: "bg-amber-50 text-amber-700 ring-amber-600/20",
};

const RoleBadge = ({ userType }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ${ROLE_BADGE[userType] || "bg-slate-100 text-slate-600 ring-slate-500/20"}`}>
    {labelFor(ADMIN_USER_TYPE_OPTIONS, userType)}
  </span>
);

const fullName = (u) => [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.username || "—";

const AdminUsersList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const currentRole = getCurrentRole();
  const canAccess = currentRole === "SuperAdmin";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [page, setPage] = useState(() => Number(searchParams.get("page") || 1));
  const [searchInput, setSearchInput] = useState(() => searchParams.get("search") || "");
  const [searchFilter, setSearchFilter] = useState(() => searchParams.get("search") || "");
  const [userType, setUserType] = useState(() => searchParams.get("user_type") || "");
  const [isActive, setIsActive] = useState(() => searchParams.get("is_active") || "");
  const [pagination, setPagination] = useState({ page: 1, items: PAGE_SIZE, total: 0, total_pages: 0 });

  const debounceRef = useRef(null);
  const toastTimerRef = useRef(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  };
  useEffect(() => () => toastTimerRef.current && clearTimeout(toastTimerRef.current), []);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const [all, active, inactive, sa] = await Promise.all([
        listAdminUsers({ items: 1 }),
        listAdminUsers({ items: 1, is_active: true }),
        listAdminUsers({ items: 1, is_active: false }),
        listAdminUsers({ items: 1, user_type: "wallet_superadmin" }),
      ]);
      setStats({
        total: all?.msg?.pagination?.total ?? 0,
        active: active?.msg?.pagination?.total ?? 0,
        inactive: inactive?.msg?.pagination?.total ?? 0,
        superadmins: sa?.msg?.pagination?.total ?? 0,
      });
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  // Keep the URL in sync with active filters (shareable / refresh-safe).
  useEffect(() => {
    const p = new URLSearchParams();
    if (page > 1) p.set("page", String(page));
    if (searchFilter.trim()) p.set("search", searchFilter.trim());
    if (userType) p.set("user_type", userType);
    if (isActive) p.set("is_active", isActive);
    setSearchParams(p, { replace: true });
  }, [page, searchFilter, userType, isActive, setSearchParams]);

  // Debounce the free-text search.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchFilter(searchInput.trim());
      setPage(1);
    }, 500);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !canAccess) return;
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, canAccess]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !canAccess) return;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await listAdminUsers({
          page, items: PAGE_SIZE, search: searchFilter, user_type: userType, is_active: isActive,
        });
        const items = resp?.msg?.items || [];
        const pg = resp?.msg?.pagination;
        setUsers(Array.isArray(items) ? items : []);
        if (pg) setPagination({ ...pg, total_pages: Math.ceil((pg.total || 0) / (pg.items || PAGE_SIZE)) });
      } catch (err) {
        setError(err.message || "Failed to load admin users.");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, canAccess, page, searchFilter, userType, isActive]);

  const handleToggle = async (user) => {
    const next = !user.is_active;
    setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, is_active: next } : u)));
    try {
      await updateAdminUserStatus(user.id, next);
      showToast("success", `${fullName(user)} ${next ? "activated" : "deactivated"}.`);
      loadStats();
    } catch (err) {
      // Revert on failure (e.g. "Cannot deactivate the last active Super Admin").
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, is_active: user.is_active } : u)));
      showToast("error", err.message || "Failed to update status.");
    }
  };

  const handleCreated = (created) => {
    showToast("success", `${created ? fullName(created) : "Admin user"} created.`);
    setPage(1);
    setSearchFilter("");
    setSearchInput("");
    loadStats();
    // Re-fetch page 1 explicitly in case filters were already at defaults.
    listAdminUsers({ page: 1, items: PAGE_SIZE }).then((resp) => {
      setUsers(resp?.msg?.items || []);
      const pg = resp?.msg?.pagination;
      if (pg) setPagination({ ...pg, total_pages: Math.ceil((pg.total || 0) / (pg.items || PAGE_SIZE)) });
    }).catch(() => { });
  };

  const hasFilters = searchFilter || userType || isActive;
  const clearFilters = () => { setSearchInput(""); setUserType(""); setIsActive(""); setPage(1); };

  if (!canAccess) {
    return (
      <div className="flex justify-center items-center h-64 text-center">
        <div>
          <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only a Super Admin can manage admin users.</p>
        </div>
      </div>
    );
  }

  const inputCls =
    "w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50";

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/subscriber-wallet")}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all"
              aria-label="Back to home"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">Admin Users</h1>
              <p className="text-sm text-gray-400">Create &amp; manage Admin Panel accounts</p>
            </div>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="self-start sm:self-auto px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 shadow-sm flex items-center gap-2 transition-all"
          >
            <Plus className="w-4 h-4" /> Create Admin User
          </button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {KPI_CARDS.map((c, i) => (
            <StatCard key={c.key} index={i} icon={c.icon} label={c.label} accent={c.accent}
              loading={statsLoading} value={stats?.[c.key] ?? 0} />
          ))}
        </div>

        {/* Sticky filter bar */}
        <div className="sticky top-4 z-20 bg-white rounded-2xl shadow-sm border border-gray-200 p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className={inputCls} placeholder="Search name, username, email or mobile"
              value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-400">Role</span>
              <SegmentedControl size="sm" value={userType} onChange={(v) => { setUserType(v); setPage(1); }}
                options={[{ value: "", label: "All" }, ...ADMIN_USER_TYPE_OPTIONS]} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-400">Status</span>
              <SegmentedControl size="sm" value={isActive} onChange={(v) => { setIsActive(v); setPage(1); }}
                options={[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }]} />
            </div>
            {hasFilters && (
              <button onClick={clearFilters} className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700">
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {error && <div className="px-6 py-3 text-sm text-red-700 bg-red-50 border-b border-red-100">{error}</div>}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {["User", "Contact", "Role", "Created", "Active"].map((h, i) => (
                    <th key={i} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-16 text-center">
                      {loading ? (
                        <div className="flex flex-col items-center gap-2 text-gray-400">
                          <Loader2 className="w-6 h-6 animate-spin" /><span className="text-sm">Loading admin users…</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3 text-gray-400">
                          <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center">
                            <Users className="w-6 h-6 text-gray-300" />
                          </div>
                          <p className="text-sm font-medium text-gray-500">No admin users found</p>
                          <button onClick={() => setModalOpen(true)} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                            + Create your first admin user
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} className={`hover:bg-blue-50/40 transition-colors ${u.is_active ? "" : "bg-gray-50/50"}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 flex items-center justify-center font-semibold text-sm shrink-0">
                            {fullName(u).charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className={`text-sm font-medium truncate ${u.is_active ? "text-gray-900" : "text-gray-400"}`}>{fullName(u)}</p>
                            <p className="text-xs text-gray-400 truncate">@{u.username}{u.user_role ? ` · ${u.user_role}` : ""}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-gray-400" />{u.email || "—"}</div>
                        {u.mobile && <div className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5"><Phone className="w-3 h-3 text-gray-300" />{u.mobile}</div>}
                      </td>
                      <td className="px-6 py-4"><RoleBadge userType={u.user_type} /></td>
                      {/* <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{u.last_login ? formatDateTime(u.last_login) : "Never"}</td> */}
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{formatDateTime(u.created_at)}</td>
                      <td className="px-6 py-4">
                        <button type="button" role="switch" aria-checked={u.is_active} onClick={() => handleToggle(u)}
                          className="relative inline-flex focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-full"
                          title={u.is_active ? "Deactivate" : "Activate"}>
                          <span className={`block w-10 h-5 rounded-full transition-colors ${u.is_active ? "bg-emerald-500" : "bg-gray-300"}`} />
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${u.is_active ? "translate-x-5" : "translate-x-0"}`} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.items + 1}–
                {Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center gap-2">
                <button disabled={pagination.page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${pagination.page === 1 ? "text-gray-300 border-gray-200 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50 border-gray-300"}`}>Prev</button>
                <button disabled={pagination.page >= (pagination.total_pages || 1)} onClick={() => setPage((p) => Math.min(pagination.total_pages || 1, p + 1))}
                  className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${pagination.page >= (pagination.total_pages || 1) ? "text-gray-300 border-gray-200 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50 border-gray-300"}`}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateAdminUserModal open={modalOpen} onClose={() => setModalOpen(false)} onCreated={handleCreated} />
      <Toast toast={toast} />
    </div>
  );
};

export default AdminUsersList;
